import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from 'sonner';
import { API } from '../App';
import { 
  Flag, 
  CheckCircle2, 
  User,
  Mail,
  Phone,
  Globe,
  IdCard,
  Calendar,
  Building2,
  Loader2,
  ArrowLeft
} from 'lucide-react';

const KOGL_LOGO = "https://customer-assets.emergentagent.com/job_magical-kenya-golf/artifacts/ft1exgdt_KOGL.png";

// Professional body options
const professionalBodyOptions = [
  'Kenya Golf Union (KGU)',
  'Professional Golfers Association (PGA)',
  'Ladies Professional Golf Association (LPGA)',
  'European Tour',
  'DP World Tour',
  'PGA Tour',
  'Asian Tour',
  'Sunshine Tour',
  'Other'
];

// Nationality options (common African and international)
const nationalityOptions = [
  'Kenyan',
  'Ugandan',
  'Tanzanian',
  'Rwandan',
  'Ethiopian',
  'South African',
  'Nigerian',
  'Ghanaian',
  'Zimbabwean',
  'British',
  'American',
  'Other'
];

export default function SafariTourRegisterPage() {
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    id_passport_number: '',
    nationality: '',
    nationality_other: '',
    email: '',
    phone: '',
    professional_body: '',
    professional_body_other: '',
    image_consent: false,
    declaration_accepted: false,
    typed_signature: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const requiredFields = [
      { field: 'full_name', label: 'Full Name' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'id_passport_number', label: 'ID/Passport Number' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'email', label: 'Email Address' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'professional_body', label: 'Professional Body' },
      { field: 'typed_signature', label: 'Typed Signature' }
    ];

    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        toast.error(`Please enter your ${label}`);
        return false;
      }
    }

    // Check if Other nationality is selected and value provided
    if (formData.nationality === 'Other' && !formData.nationality_other?.trim()) {
      toast.error('Please specify your nationality');
      return false;
    }

    // Check if Other professional body is selected and value provided
    if (formData.professional_body === 'Other' && !formData.professional_body_other?.trim()) {
      toast.error('Please specify your professional body/golf association');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Declaration must be accepted
    if (!formData.declaration_accepted) {
      toast.error('Please accept the player declaration to proceed');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        // Resolve "Other" options to actual values
        nationality: formData.nationality === 'Other' ? formData.nationality_other : formData.nationality,
        professional_body: formData.professional_body === 'Other' ? formData.professional_body_other : formData.professional_body,
        submission_type: 'safari_tour_registration'
      };

      // Remove the _other fields from final submission
      delete submissionData.nationality_other;
      delete submissionData.professional_body_other;

      const response = await fetch(`${API}/safari-tour/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmissionId(data.registration_id);
        setSubmitted(true);
        toast.success('Registration submitted successfully!');
      } else {
        toast.error(data.detail || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div data-testid="safari-tour-register-success">
        <section className="bg-primary text-primary-foreground py-16">
          <div className="container-custom text-center">
            <img src={KOGL_LOGO} alt="KOGL" className="h-20 mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Registration Received
            </h1>
          </div>
        </section>
        
        <section className="section-spacing">
          <div className="container-custom max-w-2xl">
            <Card className="card-default text-center" data-testid="success-card">
              <CardContent className="py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold mb-4">Thank You for Registering!</h2>
                <p className="text-muted-foreground font-body mb-6">
                  Your Safari Tour player registration has been received. Our team will review your application and contact you via email regarding the next steps.
                </p>
                
                {submissionId && (
                  <div className="bg-muted p-4 rounded-lg mb-6 text-left">
                    <p className="text-sm font-body">
                      <strong>Reference Number:</strong> {submissionId}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Please save this reference number for your records.
                    </p>
                  </div>
                )}
                
                <div className="bg-muted p-4 rounded-lg mb-6 text-left">
                  <p className="text-sm font-body">
                    <strong>What happens next?</strong>
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                    <li>Your application will be reviewed by the Safari Tour administration</li>
                    <li>You will receive a confirmation email within 5-7 business days</li>
                    <li>If approved, you will receive further details about Safari Tour events</li>
                    <li>Registration does not guarantee participation in any specific event</li>
                  </ul>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6 text-left">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> This registration is subject to review and confirmation by MKO/Safari Tour administration. 
                    No payment is required at this stage.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={() => window.location.href = '/'} className="btn-primary">
                    Return to Homepage
                  </Button>
                  <Link to="/apply">
                    <Button variant="outline">
                      View Other Applications
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="safari-tour-register-page">
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-16">
        <div className="container-custom">
          <div className="flex items-center justify-center gap-6 mb-6">
            <img src={KOGL_LOGO} alt="KOGL" className="h-16 md:h-20" />
          </div>
          <div className="text-center">
            <Badge className="bg-accent text-accent-foreground mb-4 px-4 py-2">
              Safari Tour 2026
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Safari Tour Player Registration
            </h1>
            <p className="text-primary-foreground/80 text-lg font-body max-w-2xl mx-auto">
              Register for Safari Tour participation. This registration is for players wishing to participate 
              in Safari Tour events including the Uganda Open and CIO Classic.
            </p>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="bg-muted py-4 border-b">
        <div className="container-custom">
          <Link to="/apply" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-body">Back to All Applications</span>
          </Link>
        </div>
      </div>

      {/* Registration Form */}
      <section className="section-spacing">
        <div className="container-custom max-w-3xl">
          <Card className="card-default" data-testid="registration-form">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">Registration Form</CardTitle>
              <CardDescription className="font-body">
                Please fill in all required fields (*) to complete your registration. 
                All information provided will be kept confidential.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="font-subheading text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => handleChange('full_name', e.target.value)}
                        placeholder="Enter your full name as it appears on your ID"
                        required
                        data-testid="input-full-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="date_of_birth">Date of Birth *</Label>
                      <Input
                        id="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => handleChange('date_of_birth', e.target.value)}
                        required
                        data-testid="input-date-of-birth"
                      />
                    </div>
                    <div>
                      <Label htmlFor="id_passport_number">ID/Passport Number *</Label>
                      <Input
                        id="id_passport_number"
                        value={formData.id_passport_number}
                        onChange={(e) => handleChange('id_passport_number', e.target.value)}
                        placeholder="National ID or Passport number"
                        required
                        data-testid="input-id-passport"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="nationality">Nationality *</Label>
                      <Select value={formData.nationality} onValueChange={(v) => handleChange('nationality', v)}>
                        <SelectTrigger data-testid="select-nationality">
                          <SelectValue placeholder="Select your nationality" />
                        </SelectTrigger>
                        <SelectContent>
                          {nationalityOptions.map(option => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.nationality === 'Other' && (
                        <Input
                          className="mt-2"
                          value={formData.nationality_other}
                          onChange={(e) => handleChange('nationality_other', e.target.value)}
                          placeholder="Please specify your nationality"
                          data-testid="input-nationality-other"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="font-subheading text-lg font-semibold mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="your.email@example.com"
                        required
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Mobile/Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="+254 7XX XXX XXX"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div>
                  <h3 className="font-subheading text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Professional Body / Golf Association
                  </h3>
                  <div>
                    <Label htmlFor="professional_body">Professional Body *</Label>
                    <Select value={formData.professional_body} onValueChange={(v) => handleChange('professional_body', v)}>
                      <SelectTrigger data-testid="select-professional-body">
                        <SelectValue placeholder="Select your professional body or golf association" />
                      </SelectTrigger>
                      <SelectContent>
                        {professionalBodyOptions.map(option => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.professional_body === 'Other' && (
                      <Input
                        className="mt-2"
                        value={formData.professional_body_other}
                        onChange={(e) => handleChange('professional_body_other', e.target.value)}
                        placeholder="Please specify your professional body or golf association"
                        data-testid="input-professional-body-other"
                      />
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Select the golf association or professional body you are affiliated with.
                    </p>
                  </div>
                </div>

                {/* No Payment Required Notice */}
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">No Payment Required</p>
                      <p className="text-sm text-green-700 mt-1">
                        This registration is free of charge. No membership fee or payment is required at this stage. 
                        Your application will be reviewed by the Safari Tour administration.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image Consent */}
                <div className="bg-muted p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="image_consent"
                      checked={formData.image_consent}
                      onCheckedChange={(checked) => handleChange('image_consent', checked)}
                      className="mt-1"
                      data-testid="checkbox-image-consent"
                    />
                    <Label htmlFor="image_consent" className="text-sm cursor-pointer leading-relaxed font-body">
                      <strong>Image Consent:</strong> I consent to have my image being used for publicity purposes 
                      by the Safari Tour, Kenya Open Golf Limited, and their partners.
                    </Label>
                  </div>
                </div>

                {/* Player Declaration */}
                <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="declaration_accepted"
                      checked={formData.declaration_accepted}
                      onCheckedChange={(checked) => handleChange('declaration_accepted', checked)}
                      className="mt-1"
                      required
                      data-testid="checkbox-declaration"
                    />
                    <Label htmlFor="declaration_accepted" className="text-sm cursor-pointer leading-relaxed font-body">
                      <strong>Player Declaration *</strong><br />
                      I have read and understood the conditions of play for Safari Tour events. 
                      I agree to abide by the code of conduct published by the Safari Tour and 
                      Kenya Open Golf Limited. I confirm that all information provided in this 
                      registration is accurate and complete.
                    </Label>
                  </div>
                </div>

                {/* Typed Signature */}
                <div>
                  <h3 className="font-subheading text-lg font-semibold mb-4 flex items-center gap-2">
                    <IdCard className="w-5 h-5 text-primary" />
                    Confirmation
                  </h3>
                  <div>
                    <Label htmlFor="typed_signature">Type Your Full Name as Signature *</Label>
                    <Input
                      id="typed_signature"
                      value={formData.typed_signature}
                      onChange={(e) => handleChange('typed_signature', e.target.value)}
                      placeholder="Type your full name to confirm this registration"
                      required
                      data-testid="input-signature"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      By typing your name above, you confirm that the information provided is accurate 
                      and you agree to the terms of this registration.
                    </p>
                  </div>
                </div>

                {/* Privacy Notice */}
                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
                  <strong>Privacy Notice:</strong> The personal information collected in this form will be used 
                  solely for the purpose of Safari Tour registration and administration. Your data will be 
                  processed in accordance with KOGL's privacy policy and applicable data protection regulations.
                  <Link to="/contact" className="text-primary hover:underline ml-1">
                    Contact us
                  </Link> for any privacy-related queries.
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full btn-primary h-14"
                  disabled={submitting}
                  data-testid="submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting Registration...
                    </>
                  ) : (
                    'Submit Registration'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
